/**
 * @fileoverview `data-fg-signup` / `data-fg-login` — signup and login
 * tracking with no code, for pages that can't run any.
 * @module features/core/declarativeAuth
 *
 * ```html
 * <button data-fg-signup="google">Sign up with Google</button>
 * <form data-fg-signup="email" data-fg-source="pricing">…</form>
 * <a href="/login" data-fg-login>Log in</a>
 * ```
 *
 * The attribute's value is the method (`email` when empty). `data-fg-userid`
 * and `data-fg-source` are optional, on the element or its form. With no
 * user id the event is keyed on the visitor, which is all the funnel needs.
 *
 * A form — or a submit button inside one — counts on submit, not on click,
 * so a click that only shows a validation error isn't a signup. Each kind
 * counts once per page load: a double click is one signup.
 */

export type AuthMode = "signup" | "login";

export interface AuthIntent {
  mode: AuthMode;
  method: string;
  userId?: string;
  source?: string;
}

/** The slice of an element this reads — so it can be tested without a DOM. */
export interface AuthElement {
  tagName: string;
  type?: string;
  form?: AuthElement | null;
  getAttribute(name: string): string | null;
  closest(selector: string): AuthElement | null;
}

export interface AuthSink {
  signup(userId: string, method: string, source?: string, properties?: Record<string, unknown>): unknown;
  login(userId: string, method?: string, properties?: Record<string, unknown>): unknown;
}

const MARKED = "[data-fg-signup],[data-fg-login]";

/**
 * The intent a click carries, or null. A marked form, or a submit control
 * inside a form, answers on submit instead — see {@link submitIntent}.
 */
export function clickIntent(target: AuthElement | null): AuthIntent | null {
  const marked = target?.closest(MARKED) ?? null;
  if (!marked || countsOnSubmit(marked)) return null;

  return intentOf(marked);
}

/** The intent a form submit carries: the form's own mark, or the submit button's. */
export function submitIntent(form: AuthElement | null, submitter: AuthElement | null): AuthIntent | null {
  if (!form) return null;

  const button = submitter?.closest(MARKED) ?? null;
  if (button && button.form === form) return intentOf(button, form);
  if (isMarked(form)) return intentOf(form);

  return null;
}

/**
 * Listens for marked clicks and submits and sends each kind once per page.
 * Returns a teardown. A no-op without a document.
 */
export function wireDeclarativeAuth(sink: () => AuthSink | null): () => void {
  if (typeof document === "undefined") return () => {};

  const sent = new Set<AuthMode>();

  const send = (intent: AuthIntent | null) => {
    if (!intent || sent.has(intent.mode)) return;

    const target = sink();
    if (!target) return;

    sent.add(intent.mode);
    try {
      const pending =
        intent.mode === "signup"
          ? target.signup(intent.userId ?? "", intent.method, intent.source, { trigger: "attribute" })
          : target.login(intent.userId ?? "", intent.method, { trigger: "attribute", ...(intent.source ? { source: intent.source } : {}) });
      void Promise.resolve(pending).catch(() => {});
    } catch {
      /* tracking never breaks the page */
    }
  };

  const onClick = (event: Event) => send(clickIntent(event.target as unknown as AuthElement | null));
  const onSubmit = (event: Event) => {
    const submitter = (event as SubmitEvent).submitter as unknown as AuthElement | null;
    send(submitIntent(event.target as unknown as AuthElement | null, submitter ?? null));
  };

  document.addEventListener("click", onClick, { capture: true, passive: true });
  document.addEventListener("submit", onSubmit, { capture: true });

  return () => {
    document.removeEventListener("click", onClick, { capture: true });
    document.removeEventListener("submit", onSubmit, { capture: true });
  };
}

function intentOf(element: AuthElement, form: AuthElement | null = element.form ?? null): AuthIntent {
  const mode: AuthMode = element.getAttribute("data-fg-signup") !== null ? "signup" : "login";
  const method = (element.getAttribute(`data-fg-${mode}`) ?? "").trim() || "email";
  const read = (name: string) => (element.getAttribute(name) ?? form?.getAttribute(name) ?? "").trim() || undefined;

  return { mode, method, userId: read("data-fg-userid"), source: read("data-fg-source") };
}

function isMarked(element: AuthElement): boolean {
  return element.getAttribute("data-fg-signup") !== null || element.getAttribute("data-fg-login") !== null;
}

function countsOnSubmit(element: AuthElement): boolean {
  if (element.tagName === "FORM") return true;
  return Boolean(element.form) && (element.type ?? "").toLowerCase() === "submit";
}
